// composables/useMemberReferral.js
//
// State for the member-referral screen (/referral/). Owns two independent reads:
//
//   GET /referral-code  -> my code + share link + reward rates + money buckets
//   GET /referrals      -> who I referred, newest first
//
// No onMounted here — the caller owns invocation. P1-C wires refresh() to F7
// page-enter so the screen refetches on return. There is NO polling timer, by
// decision: the owner reward is admin-approved and can lag hours, so a ticking
// interval would fake liveness the data cannot deliver.

import { computed, ref } from 'vue';
import {
  REFERRAL_CLIENT_ERROR_CODE,
  REFERRAL_ERROR_CODE,
  REFERRAL_NOT_ELIGIBLE_REASON,
} from '@/constants/referral';
import { referralService } from '@/services/referral.service';

// Safe defaults. Spread a fresh copy on every assignment — a frozen literal handed
// straight to a component would throw on any accidental write.
const EMPTY_REWARDS = Object.freeze({
  joinedMemberCents: 0,
  ownerCents: 0,
  activationThresholdCents: 0,
});

const EMPTY_STATS = Object.freeze({
  totalReferred: 0,
  pending: 0,
  granted: 0,
  rewarded: 0,
  potentialOwnerCents: 0,
  awaitingApprovalCents: 0,
  earnedOwnerCents: 0,
});

const EMPTY_META = Object.freeze({ total: 0, returned: 0, hasMore: false });

/**
 * Pull `{ code, reason }` out of a rejected referral request.
 *
 * ⚠️ These endpoints answer with a SINGULAR top-level `error` OBJECT:
 *
 *     { "error": { "code": "REFERRAL_NOT_ELIGIBLE", "reason": "not_activated" } }
 *
 * That is NOT the JSON:API `errors[0].code` ARRAY every other composable in this
 * repo reads (compare useWalletPoolShareSummary.js:4-7). Both shapes are valid
 * JSON and neither throws against the other, so "correcting" this to the array
 * form fails silently: every referral error would fall through to the generic
 * copy and `not_activated` — the state every brand-new member is in — would stop
 * telling them to activate. Leave it singular.
 *
 * @returns {{code: string|null, reason: string|null}}
 */
function extractReferralError(err) {
  const envelope = err?.response?.data?.error;
  const code = typeof envelope?.code === 'string' ? envelope.code : null;
  const reason = typeof envelope?.reason === 'string' ? envelope.reason : null;

  if (code) return { code, reason };

  // A 429 is the framework throttle (30/min) and carries no `error` envelope at
  // all — but it is a distinct, member-actionable state ("try again in a moment"),
  // not a generic failure. Reachable in normal use: the screen refetches on every
  // page-enter, so tabbing in and out quickly can trip it.
  if (err?.response?.status === 429) {
    return { code: REFERRAL_CLIENT_ERROR_CODE.RATE_LIMITED, reason: null };
  }

  return { code: null, reason: null };
}

// The backend evaluates eligibility in ONE fixed order — see
// ResolvesMemberReferralEligibility::resolveEligibleMembership():
//
//   REFERRAL_DISABLED -> supplier_membership -> not_activated -> REFERRAL_PROGRAM_UNAVAILABLE
//
// The consequence to preserve: a non-activated member can NEVER see
// REFERRAL_PROGRAM_UNAVAILABLE — they always get not_activated first. Ranking
// these the other way round would render a state that cannot occur.
const PRECEDENCE = [
  (e) => e.code === REFERRAL_ERROR_CODE.DISABLED,
  (e) =>
    e.code === REFERRAL_ERROR_CODE.NOT_ELIGIBLE &&
    e.reason === REFERRAL_NOT_ELIGIBLE_REASON.SUPPLIER_MEMBERSHIP,
  (e) =>
    e.code === REFERRAL_ERROR_CODE.NOT_ELIGIBLE &&
    e.reason === REFERRAL_NOT_ELIGIBLE_REASON.NOT_ACTIVATED,
  (e) => e.code === REFERRAL_ERROR_CODE.PROGRAM_UNAVAILABLE,
];

/** Pick the failure the member should be told about, in the backend's own order. */
function mostSignificant(candidates) {
  if (candidates.length === 0) return null;
  for (const matches of PRECEDENCE) {
    const hit = candidates.find(matches);
    if (hit) return hit;
  }
  // Unrecognized (network drop, 500, unmapped future code) — surface the first
  // rather than nothing, so the screen renders generic copy instead of a blank.
  return candidates[0];
}

const I18N_KEY_BY_CODE = Object.freeze({
  [REFERRAL_ERROR_CODE.PROGRAM_UNAVAILABLE]: 'referral.errors.programUnavailable',
  [REFERRAL_ERROR_CODE.DISABLED]: 'referral.errors.disabled',
  [REFERRAL_CLIENT_ERROR_CODE.RATE_LIMITED]: 'referral.errors.rateLimited',
  [REFERRAL_CLIENT_ERROR_CODE.CODE_INACTIVE]: 'referral.errors.codeInactive',
});

// The two 403s share one `code`, so the reason is the only discriminator — and
// they need genuinely different copy: not_activated is actionable ("activate your
// membership"), supplier_membership is not.
const I18N_KEY_BY_NOT_ELIGIBLE_REASON = Object.freeze({
  [REFERRAL_NOT_ELIGIBLE_REASON.NOT_ACTIVATED]: 'referral.errors.notActivated',
  [REFERRAL_NOT_ELIGIBLE_REASON.SUPPLIER_MEMBERSHIP]: 'referral.errors.supplierMembership',
});

/**
 * Map a referral error onto an i18n key under `referral.errors.*`.
 *
 * Always returns a resolvable key — an unmapped code falls back to generic copy
 * rather than echoing a raw key path at a member, which is what vue-i18n renders
 * for a miss. An unknown `reason` on a known NOT_ELIGIBLE also falls back: we do
 * not guess which recovery action to prescribe.
 *
 * @param {string|null} code
 * @param {string|null} [reason]
 * @param {string} [fallback]
 * @returns {string} an i18n key
 */
export function referralErrorToI18nKey(code, reason = null, fallback = 'referral.errors.generic') {
  if (code === REFERRAL_ERROR_CODE.NOT_ELIGIBLE) {
    return I18N_KEY_BY_NOT_ELIGIBLE_REASON[reason] ?? fallback;
  }
  return I18N_KEY_BY_CODE[code] ?? fallback;
}

export function useMemberReferral() {
  // --- GET /referral-code -------------------------------------------------
  const code = ref(null);
  // Use shareLink / startParam VERBATIM. Never rebuild the t.me URL from
  // VITE_TELEGRAM_BOT_USERNAME: that env var is the AUTH bot (PoolitClubBot) while
  // the Mini App is hosted by PoolitAssistantBot, so a rebuilt link is simply dead.
  const shareLink = ref(null);
  const startParam = ref(null);
  // Load-bearing. ReferralCode::mintForMember() is a firstOrCreate that does NOT
  // filter on is_active, so a deactivated code comes back in a live-looking 200
  // carrying a dead link. Defaults to false: "no usable link" is the safe read
  // when we have no code at all.
  const isActive = ref(false);
  // The program's CURRENT per-referral rates — a promise about the next referral,
  // NOT money. `stats` is what actually happened.
  const rewards = ref({ ...EMPTY_REWARDS });
  // Authoritative for money. The three cents buckets are mutually exclusive.
  const stats = ref({ ...EMPTY_STATS });

  const codeError = ref(null);
  const codeErrorCode = ref(null);
  const codeErrorReason = ref(null);

  // --- GET /referrals ------------------------------------------------------
  // Passed through UNTOUCHED. `id` is a string (shortflake ids exceed
  // Number.MAX_SAFE_INTEGER) and must stay one — never parseInt/Number it.
  const rows = ref([]);
  const meta = ref({ ...EMPTY_META });

  const listError = ref(null);
  const listErrorCode = ref(null);
  const listErrorReason = ref(null);

  const loading = ref(false);

  // Monotonic run counter, per composable instance. refresh() is wired to F7
  // page-enter (and an obvious retry button), so two calls can overlap: enter on
  // a slow network, re-enter, and the second request may resolve first. Without a
  // guard the LATER-settling response wins unconditionally — a stale earlier
  // payload would clobber the newer one on a money surface. Each call stamps its
  // run and only the latest run is allowed to write refs or clear `loading`.
  let latestRun = 0;

  function resetCodeHalf() {
    code.value = null;
    shareLink.value = null;
    startParam.value = null;
    isActive.value = false;
    rewards.value = { ...EMPTY_REWARDS };
    stats.value = { ...EMPTY_STATS };
  }

  function resetListHalf() {
    rows.value = [];
    meta.value = { ...EMPTY_META };
  }

  function applyCode(settled) {
    if (settled.status === 'fulfilled') {
      // apiClient does NOT transform, so this is the raw body: `{ data: {…} }`.
      const data = settled.value?.data?.data ?? {};
      code.value = data.code ?? null;
      shareLink.value = data.shareLink ?? null;
      startParam.value = data.startParam ?? null;
      isActive.value = Boolean(data.isActive);
      rewards.value = { ...EMPTY_REWARDS, ...(data.rewards ?? {}) };
      stats.value = { ...EMPTY_STATS, ...(data.stats ?? {}) };
      codeError.value = null;
      codeErrorCode.value = null;
      codeErrorReason.value = null;
      return;
    }

    const { code: errCode, reason } = extractReferralError(settled.reason);
    codeError.value = settled.reason;
    codeErrorCode.value = errCode;
    codeErrorReason.value = reason;
    // Reset so a previous success can never bleed into an error render — a stale
    // code chip next to "temporarily unavailable" is worse than no chip.
    resetCodeHalf();
  }

  function applyList(settled) {
    if (settled.status === 'fulfilled') {
      const body = settled.value?.data ?? {};
      rows.value = Array.isArray(body.data) ? body.data : [];
      // Backfill missing keys the same way stats/rewards are handled above — the
      // BE contract always sends all three, but a partial `meta` (say a proxy
      // strips `hasMore`) must not leave it `undefined`, or the locked-decision-12
      // truncation line P1-C keys off `meta.hasMore` silently vanishes. Guard
      // against a non-object (array included) so a malformed body still defaults.
      const metaObj =
        body.meta && typeof body.meta === 'object' && !Array.isArray(body.meta) ? body.meta : {};
      meta.value = { ...EMPTY_META, ...metaObj };
      listError.value = null;
      listErrorCode.value = null;
      listErrorReason.value = null;
      return;
    }

    const { code: errCode, reason } = extractReferralError(settled.reason);
    listError.value = settled.reason;
    listErrorCode.value = errCode;
    listErrorReason.value = reason;
    resetListHalf();
  }

  /**
   * Fetch both halves. They fail INDEPENDENTLY and that is a real production
   * state, not an edge case: the moment an admin rotates the gift program,
   * /referral-code 404s REFERRAL_PROGRAM_UNAVAILABLE while /referrals keeps
   * serving history. Promise.allSettled is what guarantees one rejection never
   * blanks the other half — Promise.all here would throw away a perfectly good
   * list because the code endpoint was down.
   *
   * Never rejects.
   */
  async function refresh() {
    const run = ++latestRun;
    loading.value = true;
    try {
      const [codeResult, listResult] = await Promise.allSettled([
        referralService.getCode(),
        referralService.getList(),
      ]);
      // A newer refresh() superseded this one while it was in flight — drop this
      // result whole rather than let it overwrite the fresher state.
      if (run !== latestRun) return;
      applyCode(codeResult);
      applyList(listResult);
    } finally {
      // Only the latest run owns the spinner; an older run finishing must not
      // clear a spinner the newer run is still showing.
      if (run === latestRun) loading.value = false;
    }
  }

  // Screen-level failure: non-null ONLY when BOTH halves are down, i.e. nothing on
  // this screen can render. The eligibility errors (disabled / supplier_membership
  // / not_activated) come from a trait both endpoints share, so they always land
  // here together.
  //
  // The load-bearing guarantee is the SINGLE-half case: when the code endpoint
  // 404s REFERRAL_PROGRAM_UNAVAILABLE (an admin rotated the gift program) the list
  // endpoint keeps returning 200, so listError stays null, screenFailure stays
  // null, and history renders. That is the highest-value case on this screen. Only
  // if the list ALSO fails — leaving genuinely nothing to show — does this surface
  // a screen error, and mostSignificant then picks the higher-precedence code.
  const screenFailure = computed(() => {
    if (!codeError.value || !listError.value) return null;
    return mostSignificant([
      { code: codeErrorCode.value, reason: codeErrorReason.value, err: codeError.value },
      { code: listErrorCode.value, reason: listErrorReason.value, err: listError.value },
    ]);
  });

  const error = computed(() => screenFailure.value?.err ?? null);
  const errorCode = computed(() => screenFailure.value?.code ?? null);
  const errorReason = computed(() => screenFailure.value?.reason ?? null);

  return {
    // code half
    code,
    shareLink,
    startParam,
    isActive,
    rewards,
    stats,
    codeError,
    codeErrorCode,
    codeErrorReason,
    // list half
    rows,
    meta,
    listError,
    listErrorCode,
    listErrorReason,
    // whole screen
    loading,
    error,
    errorCode,
    errorReason,
    refresh,
    toI18nKey: referralErrorToI18nKey,
  };
}
